import axios from 'axios'
import { formatDateForAPI } from '@/lib/dateUtils'
import type {
  User,
  Restaurant,
  Subscription,
  Bill,
  Food,
  DashboardMetrics,
  AnalyticsData,
  ProfitAnalysis,
  ApiResponse,
  EditedBill,
  EditedBillsSummary,
  DateRange,
  Customer,
  CustomersSummary
} from '@/types'

export interface ClientSubscriptionItem {
  restaurantId: string | null
  clientDatabaseName: string
  restaurantName: string
  plan: 'monthly' | 'halfyearly' | 'yearly'
  isActive: boolean
  startDate: string | null
  expiryDate: string | null
  daysRemaining: number
  status: 'active' | 'expiring' | 'expired' | 'suspended'
  activatedBy: string | null
  hasSubscription: boolean
  createdAt: string | null
  updatedAt: string | null
}

export interface OrdoreMiniLicense {
  _id: string
  restaurantName: string
  ownerName: string
  ownerPhone: string
  ownerEmail: string | null
  licenseKey: string
  status: 'ACTIVE' | 'GRACE' | 'EXPIRED' | 'REVOKED'
  validityDays: number
  issuedAt: string
  expiresAt: string
  restaurantId: string | null
  lastSyncedAt: string | null
  notes: string | null
  createdBy: string
  daysLeft: number
  createdAt: string
  updatedAt: string
}

export interface OrdoreMiniAuditLogEntry {
  _id: string
  licenseId: string
  action: 'GENERATED' | 'RENEWED' | 'REVOKED' | 'REACTIVATED' | 'SYNCED' | 'SIGNED_UP' | 'DELETED' | 'PASSWORD_RESET'
  performedBy: string | null
  note: string | null
  metadata: any
  createdAt: string
}

export interface OrdoreMiniLicenseSummary {
  active: number
  expiringThisMonth: number
  expired: number
  revoked: number
  expiringWithin7Days: number
}

export interface DeviceRegistration {
  fingerprint: string
  activationCode: string
  clientDatabaseName: string | null
  restaurantName: string | null
  assignedBy: string | null
  assignedAt: string | null
  firstSeenAt: string
  lastSeenAt: string
  isActive: boolean
  updateChannel: 'stable' | 'beta' | null   // set by admin; null = device keeps its own
  appVersion: string | null                  // last reported by the POS app
  reportedChannel: string | null
  lastIpAddress: string | null
  lastUserAgent: string | null
  machineId: string | null
  /** Restaurants whose assigned POS checked in from the same public IP (unassigned devices only) */
  likelyRestaurants: { clientDatabaseName: string; restaurantName: string }[]
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3004/api'

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout for production
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  
  // Add restaurant database name to headers for multi-database routing
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  if (user.restaurantId && user.databaseName) {
    config.headers['X-Database-Name'] = user.databaseName
    config.headers['X-Restaurant-ID'] = user.restaurantId
  }
  
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export class ApiService {
  // Authentication APIs
  static async login(email: string, password: string): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await api.post('/auth/login', { email, password })
    return response.data
  }

  static async refreshToken(): Promise<ApiResponse<{ token: string }>> {
    const response = await api.post('/auth/refresh')
    return response.data
  }

  static async logout(): Promise<ApiResponse<null>> {
    const response = await api.post('/auth/logout')
    return response.data
  }

  // Admin APIs
  static async getAllRestaurants(): Promise<ApiResponse<Restaurant[]>> {
    const response = await api.get('/admin/restaurants')
    return response.data
  }

  static async createRestaurant(restaurantData: Partial<Restaurant>): Promise<ApiResponse<Restaurant>> {
    const response = await api.post('/admin/restaurants', restaurantData)
    return response.data
  }

  static async refreshRestaurantsActivity(): Promise<ApiResponse<{ id: string; lastActivityDate: Date | null }[]>> {
    const response = await api.post('/admin/restaurants/refresh-activity')
    return response.data
  }

  static async assignRestaurantDatabase(restaurantId: string, databaseName: string): Promise<ApiResponse<Restaurant>> {
    const response = await api.post(`/admin/restaurants/${restaurantId}/assign-database`, { databaseName })
    return response.data
  }

  static async testDatabaseConnection(databaseName: string): Promise<ApiResponse<{ connected: boolean, collections: string[] }>> {
    const response = await api.post('/admin/database/test-connection', { databaseName })
    return response.data
  }

  static async updateRestaurant(restaurantId: string, updates: Partial<Restaurant>): Promise<ApiResponse<Restaurant>> {
    const response = await api.put(`/admin/restaurants/${restaurantId}`, updates)
    return response.data
  }

  static async activateSubscription(restaurantId: string, subscriptionData: Partial<Subscription>): Promise<ApiResponse<Subscription>> {
    const response = await api.post(`/admin/restaurants/${restaurantId}/subscription/activate`, subscriptionData)
    return response.data
  }

  static async deactivateRestaurant(restaurantId: string): Promise<ApiResponse<null>> {
    const response = await api.post(`/admin/restaurants/${restaurantId}/deactivate`)
    return response.data
  }

  static async createUser(userData: Partial<User> & { restaurantIds?: string[] }): Promise<ApiResponse<User>> {
    const response = await api.post('/admin/users', userData)
    return response.data
  }

  static async updateUser(userId: string, updates: Partial<User> & { password?: string; restaurantIds?: string[] }): Promise<ApiResponse<User>> {
    const response = await api.put(`/admin/users/${userId}`, updates)
    return response.data
  }

  static async changePassword(userId: string, passwordData: { currentPassword: string; newPassword: string }): Promise<ApiResponse<User>> {
    const response = await api.post(`/admin/users/${userId}/change-password`, passwordData)
    return response.data
  }

  static async resetUserCredentials(userId: string, credentialsData: { email?: string; password?: string }): Promise<ApiResponse<User>> {
    const response = await api.post(`/admin/users/${userId}/reset-credentials`, credentialsData)
    return response.data
  }

  static async deleteUser(userId: string): Promise<ApiResponse<null>> {
    const response = await api.delete(`/admin/users/${userId}`)
    return response.data
  }

  static async getAllUsers(): Promise<ApiResponse<User[]>> {
    const response = await api.get('/admin/users')
    return response.data
  }

  static async getSystemAnalytics(): Promise<ApiResponse<{
    totalRestaurants: number
    activeSubscriptions: number
    expiredSubscriptions: number
    totalRevenue: number
    expiringSubscriptions: Restaurant[]
  }>> {
    const response = await api.get('/admin/analytics')
    return response.data
  }

  static async getAllSubscriptions(): Promise<ApiResponse<Subscription[]>> {
    const response = await api.get('/admin/subscriptions')
    return response.data
  }

  // Restaurant Owner APIs - Now using dynamic database routing
  static async getBills(
    restaurantId: string,
    dateRange?: DateRange,
    category?: string,
    options?: {
      page?: number
      pageSize?: number
      status?: string
      search?: string
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    }
  ): Promise<ApiResponse<Bill[]>> {
    const params: Record<string, string> = {}
    if (dateRange) {
      params.from = formatDateForAPI(dateRange.from)
      params.to = formatDateForAPI(dateRange.to)
    }
    if (category) {
      params.category = category
    }
    if (options?.page) params.page = String(options.page)
    if (options?.pageSize) params.pageSize = String(options.pageSize)
    if (options?.status && options.status !== 'all') params.status = options.status
    if (options?.search) params.search = options.search
    if (options?.sortBy) params.sortBy = options.sortBy
    if (options?.sortOrder) params.sortOrder = options.sortOrder
    // Database routing handled by interceptor via X-Database-Name header
    const response = await api.get(`/restaurants/${restaurantId}/bills`, { params })
    return response.data
  }

  static async getEditedBills(
    restaurantId: string,
    dateRange?: DateRange
  ): Promise<ApiResponse<EditedBill[], EditedBillsSummary>> {
    const params: Record<string, string> = {}
    if (dateRange) {
      params.from = formatDateForAPI(dateRange.from)
      params.to = formatDateForAPI(dateRange.to)
    }
    const response = await api.get(`/restaurants/${restaurantId}/edited-bills`, { params })
    return response.data
  }

  /** Generic owner report served by `/reports/:report`; `dateRange` omitted for reports that aren't date-bound. */
  static async getReport<T>(restaurantId: string, report: string, dateRange?: DateRange): Promise<ApiResponse<T, unknown>> {
    const params: Record<string, string> = {}
    if (dateRange) {
      params.from = formatDateForAPI(dateRange.from)
      params.to = formatDateForAPI(dateRange.to)
    }
    const response = await api.get(`/restaurants/${restaurantId}/reports/${report}`, { params })
    return response.data
  }

  static async getCustomers(
    restaurantId: string,
    dateRange?: DateRange,
    options?: {
      page?: number
      pageSize?: number
      search?: string
      segment?: 'all' | 'new' | 'repeat' | 'vip'
      sortBy?: 'visits' | 'spend' | 'recent' | 'name' | 'loyalty'
      sortOrder?: 'asc' | 'desc'
    }
  ): Promise<ApiResponse<Customer[], CustomersSummary>> {
    const params: Record<string, string> = {}
    if (dateRange) {
      params.from = formatDateForAPI(dateRange.from)
      params.to = formatDateForAPI(dateRange.to)
    }
    if (options?.page) params.page = String(options.page)
    if (options?.pageSize) params.pageSize = String(options.pageSize)
    if (options?.search) params.search = options.search
    if (options?.segment && options.segment !== 'all') params.segment = options.segment
    if (options?.sortBy) params.sortBy = options.sortBy
    if (options?.sortOrder) params.sortOrder = options.sortOrder
    const response = await api.get(`/restaurants/${restaurantId}/customers`, { params })
    return response.data
  }

  static async getDashboardMetrics(restaurantId: string, date?: Date): Promise<ApiResponse<DashboardMetrics>> {
    const params = date ? { date: date.toISOString() } : {}
    const response = await api.get(`/restaurants/${restaurantId}/dashboard`, { params, timeout: 30000 })
    return response.data
  }

  static async getDashboardAiInsight(restaurantId: string): Promise<ApiResponse<{ hasData: boolean; title: string; body: string; isToday: boolean; label: string; updatedAt?: string }>> {
    const response = await api.get(`/restaurants/${restaurantId}/dashboard/ai-insight`, { timeout: 30000 })
    return response.data
  }

  // Combined ("All Restaurants") reports for Head of Restaurant owners —
  // aggregate across every restaurant assigned to the account.
  static async getCombinedDashboardMetrics(date?: Date): Promise<ApiResponse<any>> {
    const params = date ? { date: date.toISOString() } : {}
    const response = await api.get('/restaurants/combined/dashboard', { params, timeout: 30000 })
    return response.data
  }

  static async getCombinedBills(dateRange?: DateRange, options?: {
    page?: number
    pageSize?: number
    status?: string
    search?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }): Promise<ApiResponse<any>> {
    const params: Record<string, string> = {}
    if (dateRange) {
      params.from = formatDateForAPI(dateRange.from)
      params.to = formatDateForAPI(dateRange.to)
    }
    if (options?.page) params.page = String(options.page)
    if (options?.pageSize) params.pageSize = String(options.pageSize)
    if (options?.status && options.status !== 'all') params.status = options.status
    if (options?.search) params.search = options.search
    if (options?.sortBy) params.sortBy = options.sortBy
    if (options?.sortOrder) params.sortOrder = options.sortOrder
    const response = await api.get('/restaurants/combined/bills', { params })
    return response.data
  }

  static async getFoods(restaurantId: string): Promise<ApiResponse<Food[]>> {
    const response = await api.get(`/restaurants/${restaurantId}/foods`)
    return response.data
  }

  // Get bills directly from specific database (for admin cross-restaurant analytics)
  static async getBillsFromDatabase(databaseName: string, dateRange?: DateRange): Promise<ApiResponse<Bill[]>> {
    const params = dateRange ? {
      from: dateRange.from.toISOString(),
      to: dateRange.to.toISOString()
    } : {}
    const response = await api.get(`/admin/database/${databaseName}/bills`, { params })
    return response.data
  }

  static async getFoodsFromDatabase(databaseName: string): Promise<ApiResponse<Food[]>> {
    const response = await api.get(`/admin/database/${databaseName}/foods`)
    return response.data
  }

  static async getAnalytics(restaurantId: string, dateRange?: DateRange): Promise<ApiResponse<AnalyticsData>> {
    const params = dateRange ? {
      from: formatDateForAPI(dateRange.from),
      to: formatDateForAPI(dateRange.to)
    } : {}
    console.log('📅 API getAnalytics called with params:', params)
    const response = await api.get(`/restaurants/${restaurantId}/analytics`, { params })
    console.log('📊 API getAnalytics response:', response.data)
    return response.data
  }

  static async getProfitAnalysis(restaurantId: string, dateRange?: DateRange): Promise<ApiResponse<ProfitAnalysis>> {
    const params = dateRange ? {
      from: formatDateForAPI(dateRange.from),
      to: formatDateForAPI(dateRange.to)
    } : {}
    console.log('📅 API getProfitAnalysis called with params:', params)
    const response = await api.get(`/restaurants/${restaurantId}/profit-analysis`, { params })
    console.log('📊 API getProfitAnalysis response:', response.data)
    return response.data
  }

  static async getHourlyDistribution(restaurantId: string, dateRange?: DateRange): Promise<ApiResponse<any>> {
    const params = dateRange ? {
      from: formatDateForAPI(dateRange.from),
      to: formatDateForAPI(dateRange.to)
    } : {}
    console.log('📅 API getHourlyDistribution called with params:', params)
    const response = await api.get(`/restaurants/${restaurantId}/hourly-distribution`, { params })
    console.log('📊 API getHourlyDistribution response:', response.data)
    return response.data
  }

  static async getPurchaseReport(restaurantId: string, dateRange?: DateRange): Promise<ApiResponse<any>> {
    const params = dateRange ? {
      from: formatDateForAPI(dateRange.from),
      to: formatDateForAPI(dateRange.to)
    } : {}
    const response = await api.get(`/restaurants/${restaurantId}/purchase-report`, { params })
    return response.data
  }

  static async getProfitReport(restaurantId: string, dateRange?: DateRange): Promise<ApiResponse<any>> {
    const params = dateRange ? {
      from: formatDateForAPI(dateRange.from),
      to: formatDateForAPI(dateRange.to)
    } : {}
    const response = await api.get(`/restaurants/${restaurantId}/profit-report`, { params })
    return response.data
  }

  static async getIndentReport(
    restaurantId: string,
    dateRange?: DateRange,
    options?: { period?: string; outlet?: string }
  ): Promise<ApiResponse<any>> {
    const params: Record<string, string> = dateRange ? {
      from: formatDateForAPI(dateRange.from),
      to: formatDateForAPI(dateRange.to)
    } : {}
    if (options?.period) params.period = options.period
    if (options?.outlet && options.outlet !== 'all') params.outlet = options.outlet
    const response = await api.get(`/restaurants/${restaurantId}/indent-report`, { params })
    return response.data
  }

  static async getSubscriptionStatus(restaurantId: string): Promise<ApiResponse<{
    subscription: Subscription
    restaurant: Restaurant
    daysRemaining: number
  }>> {
    const response = await api.get(`/restaurants/${restaurantId}/subscription`)
    return response.data
  }

  // Subscription APIs
  static async checkSubscriptionValidity(restaurantId: string): Promise<ApiResponse<{
    isValid: boolean
    daysRemaining: number
    status: string
  }>> {
    const response = await api.get(`/restaurants/subscriptions/${restaurantId}/validity`)
    return response.data
  }

  static async getSubscriptionHistory(restaurantId: string): Promise<ApiResponse<Subscription[]>> {
    const response = await api.get(`/subscriptions/${restaurantId}/history`)
    return response.data
  }

  static async requestRenewal(restaurantId: string, contactInfo: {
    message: string
    phone: string
  }): Promise<ApiResponse<null>> {
    const response = await api.post(`/subscriptions/${restaurantId}/request-renewal`, contactInfo)
    return response.data
  }

  static async submitRenewalRequest(renewalData: {
    restaurantId: string
    restaurantName: string
    ownerName: string
    ownerEmail: string
    ownerPhone: string
    currentPlan: string
    currentAmount: number
    subscriptionEndDate: string | undefined
    daysRemaining: number
    requestedAt: string
    message: string
    databaseName: string
  }): Promise<ApiResponse<null>> {
    const response = await api.post(`/restaurants/database/${renewalData.databaseName}/renewal-request`, renewalData)
    return response.data
  }

  // Revenue Channel APIs
  static async getRevenueByChannel(restaurantId: string, dateRange?: DateRange): Promise<ApiResponse<{
    [key: string]: {
      revenue: number
      orders: number
      percentage: number
    }
  }>> {
    const params = dateRange ? {
      from: dateRange.from.toISOString(),
      to: dateRange.to.toISOString()
    } : {}
    const response = await api.get(`/restaurants/${restaurantId}/revenue-by-channel`, { params })
    return response.data
  }

  static async getStaffPerformance(restaurantId: string, dateRange?: DateRange): Promise<ApiResponse<{
    [key: string]: {
      ordersServed: number
      revenueGenerated: number
      averageOrderValue: number
    }
  }>> {
    const params = dateRange ? {
      from: dateRange.from.toISOString(),
      to: dateRange.to.toISOString()
    } : {}
    const response = await api.get(`/restaurants/${restaurantId}/staff-performance`, { params })
    return response.data
  }

  static async getCategoryAnalysis(restaurantId: string, dateRange?: DateRange): Promise<ApiResponse<{
    categories: Array<{
      name: string
      ordersCount: number
      itemsSold: number
      revenue: number
      percentage: number
      topItems: Array<{ name: string; quantity: number; revenue: number; baseRevenue: number; chargesAmount: number }>
    }>
    summary: {
      totalCategories: number
      totalOrders: number
      totalItems: number
      totalRevenue: number
    }
  }>> {
    const params = dateRange ? {
      from: formatDateForAPI(dateRange.from),
      to: formatDateForAPI(dateRange.to)
    } : {}
    const response = await api.get(`/restaurants/${restaurantId}/category-analysis`, { params })
    return response.data
  }

  static async getCancellationReport(restaurantId: string, dateRange?: DateRange): Promise<ApiResponse<any>> {
    const params = dateRange ? {
      from: formatDateForAPI(dateRange.from),
      to: formatDateForAPI(dateRange.to)
    } : {}
    const response = await api.get(`/restaurants/${restaurantId}/cancellation-report`, { params })
    return response.data
  }

  static async getExpenseReport(restaurantId: string, dateRange?: DateRange): Promise<ApiResponse<any>> {
    const params = dateRange ? {
      from: formatDateForAPI(dateRange.from),
      to: formatDateForAPI(dateRange.to)
    } : {}
    const response = await api.get(`/restaurants/${restaurantId}/expense-report`, { params })
    return response.data
  }

  static async getExpenseCategories(restaurantId: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/restaurants/${restaurantId}/expense-categories`)
    return response.data
  }

  static async createExpense(restaurantId: string, data: {
    date: string
    category: string
    newCategory?: string
    name?: string
    amount: number
    paymentType: string
    description?: string
  }): Promise<ApiResponse<any>> {
    const response = await api.post(`/restaurants/${restaurantId}/expenses`, data)
    return response.data
  }

  static async updateExpense(restaurantId: string, expenseId: string, data: {
    date: string
    category: string
    newCategory?: string
    name?: string
    amount: number
    paymentType: string
    description?: string
  }): Promise<ApiResponse<any>> {
    const response = await api.put(`/restaurants/${restaurantId}/expenses/${expenseId}`, data)
    return response.data
  }

  static async deleteExpense(restaurantId: string, expenseId: string): Promise<ApiResponse<null>> {
    const response = await api.delete(`/restaurants/${restaurantId}/expenses/${expenseId}`)
    return response.data
  }

  static async getPnlReport(restaurantId: string, period: 'daily' | 'monthly', date: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/restaurants/${restaurantId}/pnl-report`, { params: { period, date } })
    return response.data
  }


  // Admin Renewal Requests APIs
  static async getRenewalRequests(status?: string): Promise<ApiResponse<Array<{
    _id: string
    restaurantId: string
    restaurantName: string
    ownerName: string
    ownerEmail: string
    ownerPhone: string
    currentPlan: string
    currentAmount: number
    subscriptionEndDate: Date
    daysRemaining: number
    requestedAt: Date
    message: string
    status: 'pending' | 'contacted' | 'completed'
    adminNotes?: string
    createdAt: Date
    updatedAt: Date
  }>>> {
    const params = status ? { status } : {}
    const response = await api.get('/admin/renewal-requests', { params })
    return response.data
  }

  // Multi-Restaurant Management APIs
  static async getUserRestaurants(): Promise<ApiResponse<{ restaurants: Restaurant[] }>> {
    const response = await api.get('/restaurants/user/restaurants')
    return response.data
  }

  static async getRestaurantDetails(restaurantId: string): Promise<ApiResponse<Restaurant>> {
    const response = await api.get(`/restaurants/${restaurantId}/details`)
    return response.data
  }

  static async updateBusinessHours(
    restaurantId: string,
    businessHours: { openTime: string; closeTime: string }
  ): Promise<ApiResponse<{ businessHours: { openTime: string; closeTime: string } }>> {
    const response = await api.put(`/restaurants/${restaurantId}/business-hours`, businessHours)
    return response.data
  }

  // Portal API Management APIs

  // API Key Management
  static async generateApiKey(restaurantId: string, data: {
    name: string
    permissions?: string[]
    rateLimit?: { requestsPerMinute: number; requestsPerDay: number }
  }): Promise<ApiResponse<{
    apiKey: string
    keyPrefix: string
    id: string
    restaurantId: string
    restaurantName: string
    name: string
    permissions: string[]
    rateLimit: { requestsPerMinute: number; requestsPerDay: number }
    createdAt: Date
  }>> {
    const response = await api.post(`/admin/portal/api-keys/generate/${restaurantId}`, data)
    return response.data
  }

  static async listApiKeys(): Promise<ApiResponse<Array<{
    _id: string
    keyPrefix: string
    name: string
    restaurant: Restaurant
    isActive: boolean
    lastUsedAt?: Date
    expiresAt?: Date
    permissions: string[]
    rateLimit: { requestsPerMinute: number; requestsPerDay: number }
    createdAt: Date
  }>>> {
    const response = await api.get('/admin/portal/api-keys')
    return response.data
  }

  static async revokeApiKey(keyId: string): Promise<ApiResponse<null>> {
    const response = await api.delete(`/admin/portal/api-keys/${keyId}`)
    return response.data
  }

  static async getApiKeyUsage(keyId: string): Promise<ApiResponse<{
    apiKey: {
      _id: string
      keyPrefix: string
      name: string
      lastUsedAt?: Date
    }
    restaurant: Restaurant
    usage: {
      totalRequests: number
      last24Hours: number
      last7Days: number
      successRate: number
    }
  }>> {
    const response = await api.get(`/admin/portal/api-keys/${keyId}/usage`)
    return response.data
  }

  // Global Settings Management
  static async getGlobalSettings(): Promise<ApiResponse<{
    _id: string
    maintenanceMode: boolean
    maintenanceMessage?: string
    estimatedEndTime?: Date
    minimumVersion?: string
    latestVersion?: string
    announcement?: string
    announcementPriority: 'info' | 'warning' | 'critical'
    supportContact: string
    updatedAt: Date
    createdAt: Date
  }>> {
    const response = await api.get('/admin/portal/settings')
    return response.data
  }

  static async updateGlobalSettings(settings: {
    maintenanceMode?: boolean
    maintenanceMessage?: string
    estimatedEndTime?: Date | string
    minimumVersion?: string
    latestVersion?: string
    announcement?: string
    announcementPriority?: 'info' | 'warning' | 'critical'
    supportContact?: string
  }): Promise<ApiResponse<{
    _id: string
    maintenanceMode: boolean
    maintenanceMessage?: string
    estimatedEndTime?: Date
    minimumVersion?: string
    latestVersion?: string
    announcement?: string
    announcementPriority: 'info' | 'warning' | 'critical'
    supportContact: string
    updatedAt: Date
    createdAt: Date
  }>> {
    const response = await api.put('/admin/portal/settings', settings)
    return response.data
  }

  static async toggleMaintenanceMode(enabled: boolean, message?: string, estimatedEndTime?: Date | string): Promise<ApiResponse<{
    _id: string
    maintenanceMode: boolean
    maintenanceMessage?: string
    estimatedEndTime?: Date
    supportContact: string
    updatedAt: Date
  }>> {
    const response = await api.post('/admin/portal/settings/maintenance-mode', {
      enabled,
      message,
      estimatedEndTime
    })
    return response.data
  }

  // Banner Management
  static async updateBanner(banner: { bannerImageUrl?: string; bannerSvg?: string }): Promise<ApiResponse<{
    bannerImageUrl: string | null
    bannerSvg: string | null
  }>> {
    const response = await api.put('/admin/banner', banner)
    return response.data
  }

  // Validation Logs and Analytics
  static async getValidationLogs(filters: {
    from?: Date | string
    to?: Date | string
    result?: string
    restaurantId?: string
    limit?: number
    offset?: number
  }): Promise<ApiResponse<{
    logs: Array<{
      _id: string
      restaurantId?: Restaurant
      databaseName: string
      machineId?: string
      appVersion?: string
      ipAddress: string
      userAgent?: string
      result: string
      errorCode?: string
      responseTime: number
      timestamp: Date
    }>
    pagination: {
      total: number
      limit: number
      offset: number
      hasMore: boolean
    }
  }>> {
    const params: any = {}
    if (filters.from) params.from = typeof filters.from === 'string' ? filters.from : filters.from.toISOString()
    if (filters.to) params.to = typeof filters.to === 'string' ? filters.to : filters.to.toISOString()
    if (filters.result) params.result = filters.result
    if (filters.restaurantId) params.restaurantId = filters.restaurantId
    if (filters.limit) params.limit = filters.limit
    if (filters.offset) params.offset = filters.offset

    const response = await api.get('/admin/portal/validation-logs', { params })
    return response.data
  }

  static async getValidationAnalytics(): Promise<ApiResponse<{
    total24h: number
    total7d: number
    total30d: number
    successRate: number
    avgResponseTime: number
    failedBreakdown: Array<{
      code: string
      count: number
    }>
  }>> {
    const response = await api.get('/admin/portal/validation-analytics')
    return response.data
  }

  // Device Provisioning APIs
  static async getDevices(): Promise<ApiResponse<DeviceRegistration[]>> {
    const response = await api.get('/admin/portal/devices')
    return response.data
  }

  static async assignDevice(data: {
    fingerprint: string
    clientDatabaseName: string
    restaurantName: string
  }): Promise<ApiResponse<null>> {
    const response = await api.post('/admin/portal/devices/assign', data)
    return response.data
  }

  static async setDeviceChannel(fingerprint: string, updateChannel: 'stable' | 'beta'): Promise<ApiResponse<null>> {
    const response = await api.post('/admin/portal/devices/channel', { fingerprint, updateChannel })
    return response.data
  }

  static async removeDevice(fingerprint: string): Promise<ApiResponse<null>> {
    const response = await api.delete(`/admin/portal/devices/${encodeURIComponent(fingerprint)}`)
    return response.data
  }

  // Client Subscription APIs
  static async getAllClientSubscriptions(): Promise<ApiResponse<ClientSubscriptionItem[]>> {
    const response = await api.get('/admin/portal/client-subscriptions')
    return response.data
  }

  static async saveClientSubscription(data: {
    clientDatabaseName: string
    restaurantName: string
    plan: 'monthly' | 'halfyearly' | 'yearly'
    startDate: string
    expiryDate: string
    isActive: boolean
  }): Promise<ApiResponse<ClientSubscriptionItem>> {
    const response = await api.post('/admin/portal/client-subscriptions', data)
    return response.data
  }

  static async suspendClientSubscription(databaseName: string): Promise<ApiResponse<null>> {
    const response = await api.patch(`/admin/portal/client-subscriptions/${encodeURIComponent(databaseName)}/suspend`)
    return response.data
  }

  static async activateClientSubscription(databaseName: string): Promise<ApiResponse<null>> {
    const response = await api.patch(`/admin/portal/client-subscriptions/${encodeURIComponent(databaseName)}/activate`)
    return response.data
  }

  // Ordore Mini License Management
  static async getOrdoreMiniLicenses(params: {
    search?: string
    status?: string
    page?: number
    limit?: number
  }): Promise<ApiResponse<{
    licenses: OrdoreMiniLicense[]
    total: number
    page: number
    totalPages: number
    summary: OrdoreMiniLicenseSummary
  }>> {
    const response = await api.get('/ordore-mini/licenses', { params })
    return response.data
  }

  static async createOrdoreMiniLicense(data: {
    restaurantName: string
    ownerName: string
    ownerPhone: string
    ownerEmail?: string
    validityDays: number
    notes?: string
  }): Promise<ApiResponse<OrdoreMiniLicense>> {
    const response = await api.post('/ordore-mini/licenses', data)
    return response.data
  }

  static async renewOrdoreMiniLicense(id: string, data: {
    addDays: number
    notes?: string
  }): Promise<ApiResponse<OrdoreMiniLicense>> {
    const response = await api.patch(`/ordore-mini/licenses/${id}/renew`, data)
    return response.data
  }

  static async revokeOrdoreMiniLicense(id: string, notes?: string): Promise<ApiResponse<OrdoreMiniLicense>> {
    const response = await api.patch(`/ordore-mini/licenses/${id}/revoke`, { notes })
    return response.data
  }

  static async resetOrdoreMiniLicensePassword(id: string, notes?: string): Promise<ApiResponse<{
    license: OrdoreMiniLicense
    tempPassword: string
  }>> {
    const response = await api.patch(`/ordore-mini/licenses/${id}/reset-password`, { notes })
    return response.data
  }

  static async getOrdoreMiniLicenseAudit(id: string): Promise<ApiResponse<OrdoreMiniAuditLogEntry[]>> {
    const response = await api.get(`/ordore-mini/licenses/${id}/audit`)
    return response.data
  }

  static async deleteOrdoreMiniLicense(id: string): Promise<ApiResponse<null>> {
    const response = await api.delete(`/ordore-mini/licenses/${id}`)
    return response.data
  }

}

export default ApiService